import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useToast } from "../../../basecomp/components/provider/ToastProvider";

const ApproverDashboardVM = () => {
  const [visitors, setVisitors] = useState([]);
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [tabCounts, setTabCounts] = useState({
    today: 0,
    upcoming: 0,
    pending: 0,
    past: 0
  });
  const [errorMessage, setErrorMessage] = useState("");
  
  const navigate = useNavigate();
  const { handleToastOpen } = useToast();

  // Fetch visitors data on component mount
  useEffect(() => {
    fetchVisitors();
    // Set up a refresh interval (every 5 minutes)
    const intervalId = setInterval(() => {
      fetchVisitors(false); // Silent refresh
    }, 5 * 60 * 1000);
    
    // Clear the interval when component unmounts
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update tab counts whenever visitors data changes
  useEffect(() => {
    updateTabCounts();
  }, [visitors]);

  // Calculate and update the counts for each tab
  const updateTabCounts = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Count for each category
    const todayCount = visitors.filter(visitor => {
      if (!visitor.visit_from) return false;
      const visitDate = new Date(visitor.visit_from);
      visitDate.setHours(0, 0, 0, 0);
      return visitDate.getTime() === today.getTime();
    }).length;
    
    const upcomingCount = visitors.filter(
      visitor => 
        visitor.visit_from &&
        new Date(visitor.visit_from) > today &&
        visitor.status === "Approved"
    ).length;
    
    const pendingCount = visitors.filter(
      visitor =>
        visitor.status &&
        ["Not Processed", "Resubmitted", "On Hold"].includes(visitor.status)
    ).length;
    
    const pastCount = visitors.filter(visitor => {
      if (!visitor.visit_till || !visitor.status) return false;

      const visitTillDate = new Date(visitor.visit_till);
      visitTillDate.setHours(0, 0, 0, 0);
      return (
        visitTillDate < today &&
        ["Approved", "Rejected"].includes(visitor.status)
      );
    }).length;
    
    setTabCounts({
      today: todayCount,
      upcoming: upcomingCount,
      pending: pendingCount,
      past: pastCount
    });
  };

  const fetchVisitors = async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    }
    setErrorMessage("");
    
    try {
      const contactInfo = localStorage.getItem("contactInfo");
      console.log("contactInfo", contactInfo);
      if (!contactInfo) {
        navigate("/visitor-mgmt/login");
        return;
      }

      // Pass contactInfo as a query parameter
      const response = await axios.get(
        `${process.env.REACT_APP_BE_URL2}/visitor-mgmt/visitors/approver`,
        {
          params: { approver: contactInfo }, // Pass approver in params
        }
      );

      console.log("response.data.data", response.data.data);
      if (response.data && response.data.data) {
        setVisitors(response.data.data);
      } else {
        console.warn("Unexpected data format:", response.data);
        setVisitors([]);
        setErrorMessage("Received unexpected data format from the server");
      }
      
      if (showLoading) {
        handleToastOpen("success", "Visitor data loaded successfully");
      }
    } catch (error) {
      console.error("Error fetching visitors:", error);
      setErrorMessage("Failed to load visitor data. Please try again.");
      
      if (showLoading) {
        handleToastOpen("error", "Failed to load visitor data");
      }
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const refreshData = () => {
    fetchVisitors(true); // Show loading when manually refreshing
  };

  const filterVisitors = (tabIndex) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    switch (tabIndex) {
      case 0:
        // Today's Visitors
        return visitors.filter((visitor) => {
          if (!visitor.visit_from) return false;
          const visitDate = new Date(visitor.visit_from);
          visitDate.setHours(0, 0, 0, 0);
          return visitDate.getTime() === today.getTime();
        });
      case 1:
        // Upcoming Visitors
        return visitors.filter(
          (visitor) =>
            visitor.visit_from &&
            new Date(visitor.visit_from) > today &&
            visitor.status === "Approved"
        );
      case 2:
        // Pending Requests
        return visitors.filter(
          (visitor) =>
            visitor.status &&
            ["Not Processed", "Resubmitted", "On Hold"].includes(visitor.status)
        );
      case 3:
        // Past Requests
        return visitors.filter((visitor) => {
          if (!visitor.visit_till || !visitor.status) return false;

          const visitTillDate = new Date(visitor.visit_till);
          visitTillDate.setHours(0, 0, 0, 0);
          return (
            visitTillDate < today &&
            ["Approved", "Rejected"].includes(visitor.status)
          );
        });
      default:
        return [];
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleViewDetails = (application) => {
    navigate(`/visitor-mgmt/approve-visitor/${application.id}`, {
      state: { application },
    });
  };

  return {
    visitors,
    currentTab,
    loading,
    filterVisitors,
    handleTabChange,
    handleViewDetails,
    refreshData,
    tabCounts,
    errorMessage
  };
};

export default ApproverDashboardVM;